import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { trackEvent } from "./Analytics";

type Plan = "free" | "pro" | "premium";

interface DashboardProps {
  pageType: "default" | "detector" | "competitor";
  pageKey?:
    | "turnitin"
    | "gptzero"
    | "copyleaks"
    | "stealthwriter"
    | "undetectable-ai"
    | "ai-humaniser"
    | "ai-humanizer"
    | "ai-bypasser"
    | "bypass-ai-detection"
    | "ai-detection-remover"
    | "best-ai-humanizer-for-students";
}

interface FAQItem {
  q: string;
  a: React.ReactNode;
}

interface SEOPageConfig {
  title: string;
  description: string;
  canonical: string;
  h1: string;
  reassurance: string;
  subheading: string;
  faqs: FAQItem[];
}

// Structured SEO Mapping Engine
const SEO_CONFIG: Record<string, SEOPageConfig> = {
  default: {
    title: "DeCipher - AI Bypasser & AI Humanisers Alternative",
    description: "AI bypasser for ChatGPT, Claude, and Gemini text. Keep your original meaning and writing style while bypassing Turnitin, GPTZero, and Copyleaks detection patterns.",
    canonical: "https://deciphertext.live/",
    h1: "Bypass AI detectors while keeping your original writing style.",
    reassurance: "No fake rewrites. No fluff. Just cleaner detector-safe structure.",
    subheading: "Paste your text, preserve your meaning, and reduce AI detector triggers in seconds.",
    faqs: [
      { 
        q: "Can DeCipher reliably bypass Turnitin and other AI detectors?", 
        a: <>Yes. DeCipher is built specifically to bypass detectors that consistently score low AI probability on Turnitin, GPTZero, Originality.ai, and other major detectors. Thousands of students and professionals already rely on it daily.</> 
      },
      { 
        q: "Will the output still be my own writing?", 
        a: <>Absolutely. Unlike tools that force heavy "humanizer" rewrites, DeCipher keeps your intent, structure, and voice while removing detector-triggering AI patterns.</> 
      },
      { 
        q: "What are the word and submission limits?", 
        a: <>Free plan: 1,200 words per submission (3 per day).<br />Pro plan ($5.99/month): 3,000 words per submission + 50 submissions per day.<br />Premium plan ($10.99/month): 12,000 words per submission + unlimited submissions.</> 
      },
      { 
        q: "How is my data and privacy protected?", 
        a: <>Your privacy is our top priority. We never store your submitted text after processing is complete, and all data is handled with enterprise-grade encryption.</> 
      }
    ]
  },
  turnitin: {
    title: "Bypass Turnitin AI Detection | 100% Undetectable Humaniser",
    description: "How to bypass Turnitin false positives seamlessly. Use DeCipher to structurally adapt your essays and research papers to show 0% AI on Turnitin similarity reports.",
    canonical: "https://deciphertext.live/bypass-turnitin",
    h1: "Bypass Turnitin AI Filters Instantly. Keep Your Academic Integrity.",
    reassurance: "Engineered specifically to beat Turnitin's latest enterprise models",
    subheading: "Get authentic, organic text structures that preserve your core thesis statement safely.",
    faqs: [
      { 
        q: "How does DeCipher defeat Turnitin AI classification rules?", 
        a: <>Turnitin scores text based on structural predictability (perplexity and burstiness metrics). DeCipher varies sentence cadence and vocabulary depth dynamically, breaking the signature patterns left behind by ChatGPT and Claude without introducing awkward grammar errors.</> 
      },
      { 
        q: "Is my text saved or sent to Turnitin's database?", 
        a: <>Never. DeCipher operates with enterprise-grade data privacy. Your text is run locally in memory during processing and is immediately wiped. We never store, leak, or share your input files.</> 
      }
    ]
  },
  gptzero: {
    title: "Bypass GPTZero AI Detection | Free Text Humaniser Tool",
    description: "Frustrated by GPTZero false flags? Learn how to change text parameters to secure a 100% human rating. Try DeCipher's targeted GPTZero bypass engine for free.",
    canonical: "https://deciphertext.live/bypass-gptzero",
    h1: "Outsmart GPTZero’s Perplexity Metrics. Secure Clean Human Scores.",
    reassurance: "No more false flags from AI grading bots",
    subheading: "Transform rigid generative text profiles into rich, human-mode patterns seamlessly.",
    faqs: [
      { 
        q: "What parameters trigger GPTZero flags?", 
        a: <>GPTZero calculates uniform line lengths and high phrase predictability. If your content lacks structural chaos, it is instantly marked as AI. DeCipher introduces natural human styling variations to completely neutralize this evaluation metric.</> 
      }
    ]
  },
  copyleaks: {
    title: "Bypass Copyleaks AI Detector | Free Undetectable Text Tool",
    description: "Convert AI text to human copy that defeats the Copyleaks pattern detector. Free alternative layout optimized to process professional and corporate materials.",
    canonical: "https://deciphertext.live/bypass-copyleaks",
    h1: "Beat Copyleaks AI Tracking Without Losing Your Brand Voice.",
    reassurance: "Optimized for marketing copy, blog articles, and SEO documents",
    subheading: "Ensure high-converting text bypasses enterprise-level pattern scanners perfectly.",
    faqs: [
      { 
        q: "Does this affect Google search optimization metrics?", 
        a: <>No. DeCipher removes patterns that trigger spam-filtering search engine flags, helping your organic content rank naturally without risking automated algorithmic downgrades.</> 
      }
    ]
  },
  "ai-humaniser": {
    title: "AI Humaniser Alternative | Keep Your Voice with DeCipher",
    description: "Looking for an AI humaniser? DeCipher keeps your original tone and argument while removing detector-triggering patterns from AI-assisted writing.",
    canonical: "https://deciphertext.live/ai-humaniser",
    h1: "AI Humaniser Alternative That Keeps Your Original Writing Voice",
    reassurance: "Built for writers who want detector-safe text without losing intent",
    subheading: "Keep your structure and meaning while reducing AI-detection risk across major detectors.",
    faqs: [
      {
        q: "How is DeCipher different from standard AI humanisers?",
        a: <>Most tools heavily rewrite your text. DeCipher focuses on detector-triggering pattern removal so your meaning, tone, and structure stay intact.</>,
      },
      {
        q: "Who is this best for?",
        a: <>Students, content writers, and professionals who need clean, readable text that stays close to their original draft.</>,
      },
    ],
  },
  "ai-humanizer": {
    title: "Best AI Humanizer Tool Alternative | DeCipher",
    description: "Use DeCipher as an AI humanizer alternative to preserve your argument and writing flow while bypassing detector patterns.",
    canonical: "https://deciphertext.live/ai-humanizer",
    h1: "Need an AI Humanizer? Use a Detector-Focused Alternative",
    reassurance: "Preserve readability while reducing detector-triggered AI signals",
    subheading: "DeCipher helps you avoid robotic rewrites and keep text naturally aligned with your original draft.",
    faqs: [
      {
        q: "Does DeCipher over-rewrite content?",
        a: <>No. It is designed to preserve your voice and intent rather than replacing your writing style with generic phrasing.</>,
      },
      {
        q: "Can I use this for long-form writing?",
        a: <>Yes. Plan limits support different use cases, from short assignments to long-form content workflows.</>,
      },
    ],
  },
  "ai-bypasser": {
    title: "AI Bypasser Tool | Bypass AI Detectors with DeCipher",
    description: "DeCipher is an AI bypasser built to reduce AI detector flags while preserving your original writing style and readability.",
    canonical: "https://deciphertext.live/ai-bypasser",
    h1: "AI Bypasser Built for Real Writing, Not Robotic Rewrites",
    reassurance: "Detector bypass logic tuned for practical writing workflows",
    subheading: "Use DeCipher to keep your structure and voice while lowering detection risk across major AI checkers.",
    faqs: [
      {
        q: "Which detectors does DeCipher target?",
        a: <>DeCipher is used for detector-focused workflows including Turnitin, GPTZero, Copyleaks, and similar AI-probability checkers.</>,
      },
      {
        q: "Is it beginner friendly?",
        a: <>Yes. Paste your text, run DeCipher, then review and copy your output. No complicated settings required.</>,
      },
    ],
  },
  "bypass-ai-detection": {
    title: "Bypass AI Detection While Keeping Your Writing Style",
    description: "Bypass AI detection without destroying readability. DeCipher preserves your original argument and voice while reducing detector-triggering patterns.",
    canonical: "https://deciphertext.live/bypass-ai-detection",
    h1: "Bypass AI Detection Without Rewriting Your Whole Draft",
    reassurance: "Preserve your writing intent while improving detector resilience",
    subheading: "Designed for users who need cleaner AI scores but still want text that sounds like them.",
    faqs: [
      {
        q: "Will this make my writing sound unnatural?",
        a: <>No. DeCipher focuses on maintaining readability and intent so the output still feels like your own writing.</>,
      },
      {
        q: "Can I test this before upgrading?",
        a: <>Yes. You can start with the free plan and upgrade once you need larger limits and higher throughput.</>,
      },
    ],
  },
  "ai-detection-remover": {
    title: "AI Detection Remover Alternative | DeCipher",
    description: "Use DeCipher as an AI detection remover alternative for detector-safe output that still preserves your original writing style.",
    canonical: "https://deciphertext.live/ai-detection-remover",
    h1: "AI Detection Remover Alternative for Practical Daily Use",
    reassurance: "Reduce detector flags while keeping your writing natural",
    subheading: "DeCipher helps users remove predictable AI patterns without flattening tone or introducing fluff.",
    faqs: [
      {
        q: "Why does detector-focused output matter?",
        a: <>Because heavy rewrites can damage clarity. DeCipher is built to improve detector outcomes while preserving your core message.</>,
      },
      {
        q: "Can teams use this workflow?",
        a: <>Yes. The process is simple enough for solo users and scalable for repeat content workflows.</>,
      },
    ],
  },
  "best-ai-humanizer-for-students": {
    title: "Best AI Humanizer for Students? Try DeCipher Instead",
    description: "For students seeking an AI humanizer, DeCipher offers detector-focused output that keeps arguments clear and close to the original draft.",
    canonical: "https://deciphertext.live/best-ai-humanizer-for-students",
    h1: "A Student-Friendly Alternative to Typical AI Humanizers",
    reassurance: "Built to keep your assignment readable and detector-safe",
    subheading: "DeCipher helps students reduce detector flags while preserving thesis flow and clarity.",
    faqs: [
      {
        q: "Will this preserve my assignment structure?",
        a: <>Yes. DeCipher prioritizes structural and tonal consistency with your original submission draft.</>,
      },
      {
        q: "Can I use this for essays and reports?",
        a: <>Yes. It is commonly used for coursework, essays, and longer report-style writing where clarity matters.</>,
      },
    ],
  },
  stealthwriter: {
    title: "Best Free StealthWriter Alternative | Try DeCipher Text",
    description: "Compare DeCipher vs StealthWriter. Discover why content creators are switching to DeCipher for cleaner formatting, faster rewriting speeds, and fairer pricing packages.",
    canonical: "https://deciphertext.live/alternatives/stealthwriter",
    h1: "Looking for a Streamlined StealthWriter Alternative? Meet DeCipher.",
    reassurance: "Cleaner layouts, premium features, and highly affordable scaling tiers",
    subheading: "No messy pricing structures or confusing output modes — just perfect, undetectable text outputs.",
    faqs: [
      { 
        q: "Why are developers and content teams shifting away from StealthWriter?", 
        a: <>Users switch because alternative platforms can produce erratic text formatting, sentence repetition, or force users through multi-tiered conversion models. DeCipher provides humanizer outputs with predictable pricing starting at just $5.99/month.</> 
      }
    ]
  },
  "undetectable-ai": {
    title: "Best Undetectable AI Alternative | Free Humaniser App",
    description: "Compare DeCipher and Undetectable AI features side-by-side. See why users choose DeCipher to escape high monthly rates and rigid credit conversion structures.",
    canonical: "https://deciphertext.live/alternatives/undetectable-ai",
    h1: "Tired of Complex Credit Models? Switch to DeCipher Today.",
    reassurance: "Flexible monthly submission access that puts the user back in control",
    subheading: "Maximize word limits without calculation gymnastics or heavy recurring subscription overhead.",
    faqs: [
      { 
        q: "How does the DeCipher workflow simplify text management?", 
        a: <>Instead of managing dynamic point transformations or buying costly credit expansions, DeCipher runs flat-fee monthly access allocations, enabling clear budget tracking for high-volume creator operations.</> 
      }
    ]
  }
};

const SHARED_FAQS: FAQItem[] = [
  {
    q: "What are the word and submission limits?",
    a: (
      <>
        Free plan: 1,200 words per submission (3 per day).<br />
        Pro plan ($5.99/month): 3,000 words per submission + 50 submissions per day.<br />
        Premium plan ($10.99/month): 12,000 words per submission + unlimited submissions.
      </>
    ),
  },
  {
    q: "How is my data and privacy protected?",
    a: (
      <>
        Your privacy is our top priority. We never store your submitted text after processing is complete, and all data is handled with enterprise-grade encryption.
      </>
    ),
  },
];

const faqNodeToText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(faqNodeToText).join(" ");
  if (React.isValidElement(node)) {
    if (node.type === "br") return "\n";
    const children = (node.props as { children?: React.ReactNode }).children;
    return faqNodeToText(children);
  }
  return "";
};

const TRUST_SIGNALS = [
  {
    title: "Original voice intact",
    detail: "Keeps your core argument and writing cadence instead of flattening everything into generic phrasing.",
    tag: "Readability",
    icon: "✍️",
  },
  {
    title: "Detector pattern control",
    detail: "Built around reducing detector-triggering structure patterns while keeping meaning clear.",
    tag: "Detection",
    icon: "🛡️",
  },
  {
    title: "Fast paste-to-output loop",
    detail: "Run quick edits in seconds with copy-ready output that fits assignment and content workflows.",
    tag: "Speed",
    icon: "⚡",
  },
];


const REVIEW_CAROUSEL_ITEMS = [
  {
    quote: "I tested a few rewriting tools and this was one of the few that kept the structure of my original draft intact. The output felt much closer to what I actually wrote.",
    name: "Alex Carter",
    role: "University Student",
    note: "",
  },
  {
    quote: "What stood out to me was how little editing I needed to do afterwards. It preserved my writing style instead of completely rewriting the content.",
    name: "Maya Patel",
    role: "Freelance Content Writer",
    note: "",
  },
  {
    quote: "The process is incredibly straightforward. I paste my text, review the result, and move on. It fits neatly into my existing workflow.",
    name: "Jordan Mitchell",
    role: "Research Assistant",
    note: "",
  },
  {
    quote: "Most tools I've tried change my wording too much. This one keeps the original meaning and voice while making only the adjustments I need.",
    name: "Sophie Reynolds",
    role: "Marketing Coordinator",
    note: "",
  },
  {
    quote: "I like that the output still sounds like me. It doesn't introduce phrases or language that I would never normally use.",
    name: "Daniel Kim",
    role: "Business Analyst",
    note: "",
  },
  {
    quote: "The interface is simple and the results are consistent. It helps me stay productive without adding extra steps to my writing process.",
    name: "Priya Shah",
    role: "Graduate Research Student",
    note: "",
  },
];

export default function Dashboard({ pageType, pageKey }: DashboardProps) {
  const navigate = useNavigate();
  const { isLoggedIn, loading, refreshAuth } = useContext(AuthContext);

  const [plan, setPlan] = useState<Plan>("free");
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [loadingDecipher, setLoadingDecipher] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionsToday, setSubmissionsToday] = useState(0);
  const [maxSubmissionsPerDay, setMaxSubmissionsPerDay] = useState<number | "unlimited">(3);
  const [maxWordsPerSubmission, setMaxWordsPerSubmission] = useState(1200);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hasTrackedInputStart, setHasTrackedInputStart] = useState(false);
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const [sessionProcessedCount, setSessionProcessedCount] = useState<number>(() => {
    const stored = window.localStorage.getItem("decipher-session-processed");
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    return 0;
  });

  // Dynamic content ingestion selection hook
  const activeSEO = useMemo(() => {
    if (pageType !== "default" && pageKey && SEO_CONFIG[pageKey]) {
      return SEO_CONFIG[pageKey];
    }
    return SEO_CONFIG.default;
  }, [pageType, pageKey]);

  const faqItems = useMemo(
    () => (pageType === "default" ? activeSEO.faqs : [...activeSEO.faqs, ...SHARED_FAQS]),
    [activeSEO, pageType]
  );
  const upgradeTargetPlan = plan === "free" ? "Pro" : "Premium";
  const activeReview = REVIEW_CAROUSEL_ITEMS[activeReviewIndex];

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (pageType !== "default" || REVIEW_CAROUSEL_ITEMS.length <= 1) return;

    const interval = window.setInterval(() => {
      setActiveReviewIndex((prev) => (prev + 1) % REVIEW_CAROUSEL_ITEMS.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [pageType]);

  useEffect(() => {
    if (loading) return;

    if (isLoggedIn) {
      api.get("/api/users/me").then((res) => {
        setPlan(res.data.plan);
        setSubmissionsToday(res.data.submissionsToday ?? 0);
        const maxSubs = res.data.maxSubmissionsPerDay;
        setMaxSubmissionsPerDay(maxSubs === null ? "unlimited" : maxSubs);
        setMaxWordsPerSubmission(res.data.maxWordsPerSubmission);
      }).catch(() => {
        setPlan("free");
        setMaxSubmissionsPerDay(3);
        setMaxWordsPerSubmission(1200);
      });
    } else {
      setPlan("free");
      setMaxSubmissionsPerDay(3);
      setMaxWordsPerSubmission(1200);
      setSubmissionsToday(0);
    }
  }, [isLoggedIn, loading]);

  const wordCount = useMemo(() => {
    return inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
  }, [inputText]);

  const isUnlimited = maxSubmissionsPerDay === "unlimited";
  const submissionsRemaining = isUnlimited ? Infinity : maxSubmissionsPerDay - submissionsToday;
  const wordLimitExceeded = wordCount > maxWordsPerSubmission;
  const submissionLimitReached = !isUnlimited && submissionsRemaining <= 0;

  const upgradePhrases = [
    "You've reached your daily limit. Upgrade to keep writing without interruptions.",
    "Need more submissions today? Unlock higher limits with Pro.",
    "Scale your writing workflow with a plan built for higher output.",
  ];

  const randomPhrase = useMemo(() => {
    return upgradePhrases[Math.floor(Math.random() * upgradePhrases.length)];
  }, []);

  const handleDecipher = async () => {
    setLoadingDecipher(true);
    setError(null);
    trackEvent("decipher_request_started", {
      page_type: pageType,
      plan,
      word_count: wordCount,
    });
    try {
      const res = await api.post("/api/decipher", { text: inputText });
      setOutputText(res.data.outputText);
      setSubmissionsToday((prev) => prev + 1);
      setSessionProcessedCount((prev) => {
        const next = prev + 1;
        window.localStorage.setItem("decipher-session-processed", String(next));
        return next;
      });
      trackEvent("decipher_request_succeeded", {
        page_type: pageType,
        plan,
        word_count: wordCount,
      });
    } catch (err: any) {
      const msg = err.response?.data?.message || "Decipher failed";
      setError(msg);
      trackEvent("decipher_request_failed", {
        page_type: pageType,
        plan,
      });
    } finally {
      setLoadingDecipher(false);
    }
  };

  const clearInput = () => {
    if (inputText.trim()) {
      trackEvent("decipher_input_cleared", {
        page_type: pageType,
        had_output: Boolean(outputText),
      });
    }
    setInputText("");
    setError(null);
    setHasTrackedInputStart(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = e.target.value;
    setInputText(nextValue);

    if (!hasTrackedInputStart && nextValue.trim().length > 0) {
      setHasTrackedInputStart(true);
      trackEvent("decipher_input_started", {
        page_type: pageType,
      });
    }
  };

  const copyOutput = async () => {
    if (!outputText) return;
    try {
      await navigator.clipboard.writeText(outputText);
      trackEvent("decipher_output_copied", {
        page_type: pageType,
        plan,
      });
    } catch (err) {
      console.error("Copy failed");
      trackEvent("decipher_output_copy_failed", {
        page_type: pageType,
        plan,
      });
    }
  };

  const goToPricing = (source: string) => {
    trackEvent("pricing_cta_clicked", {
      page_type: pageType,
      plan,
      source,
    });
    navigate("/pricing");
  };

  const goToReview = (index: number, source: "dot" | "prev" | "next") => {
    const normalizedIndex = (index + REVIEW_CAROUSEL_ITEMS.length) % REVIEW_CAROUSEL_ITEMS.length;
    setActiveReviewIndex(normalizedIndex);
    trackEvent("review_carousel_interaction", {
      page_type: pageType,
      source,
      review_index: normalizedIndex,
    });
  };

  const handleSubmitClick = () => {
    trackEvent("decipher_submit_clicked", {
      page_type: pageType,
      plan,
      logged_in: isLoggedIn,
      submission_limit_reached: submissionLimitReached,
      word_limit_exceeded: wordLimitExceeded,
    });

    if (!isLoggedIn) {
      trackEvent("auth_modal_opened", {
        source: "submit_click",
        page_type: pageType,
      });
      setShowAuthModal(true);
      return;
    }
    if (submissionLimitReached) {
      goToPricing("submission_limit");
    } else {
      handleDecipher();
    }
  };

  return (
    <>
      <Helmet>
        <title>{activeSEO.title}</title>
        <meta name="description" content={activeSEO.description} />
        <meta
          name="keywords"
          content="AI humanisers, AI bypasser, bypass AI detection, Turnitin bypass, GPTZero bypass, Copyleaks bypass, undetectable AI text"
        />
        <meta
          name="robots"
          content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={activeSEO.title} />
        <meta property="og:description" content={activeSEO.description} />
        <meta property="og:url" content={activeSEO.canonical} />
        <meta property="og:image" content="https://deciphertext.live/logo-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={activeSEO.title} />
        <meta name="twitter:description" content={activeSEO.description} />
        <meta name="twitter:image" content="https://deciphertext.live/logo-512.png" />
        <link rel="canonical" href={activeSEO.canonical} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "DeCipher",
            "url": activeSEO.canonical,
            "description": activeSEO.description,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "DeCipher",
            "url": "https://deciphertext.live/",
            "logo": "https://deciphertext.live/logo-512.png",
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faqNodeToText(faq.a).replace(/\s+/g, " ").trim(),
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="page-shell py-10 sm:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          {submissionLimitReached && randomPhrase && (
            <div className="fixed left-1/2 top-20 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-amber-200 bg-amber-100 px-5 py-3 text-amber-900 shadow-lg">
              <span className="text-xl">⚡</span>
              <p className="max-w-md text-sm font-semibold">{randomPhrase}</p>
            </div>
          )}

          <div className="mb-8 text-center">
            <p className="mb-3 inline-flex rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
              {activeSEO.reassurance}
            </p>
            <h1 className="mx-auto mb-3 max-w-4xl text-3xl font-extrabold leading-tight text-secondary sm:text-4xl">
              {activeSEO.h1}
            </h1>
            <p className="mx-auto max-w-2xl text-sm text-textMuted sm:text-base">
              {activeSEO.subheading}
            </p>
          </div>

          <div className="ui-card-muted mb-8 p-5 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <p className="text-sm text-textMuted">Current plan</p>
                <p className="text-lg font-bold capitalize text-secondary">{plan}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`rounded-full px-3 py-1 font-semibold ${wordLimitExceeded ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  Words: {wordCount}/{maxWordsPerSubmission}
                </span>
                <span className={`rounded-full px-3 py-1 font-semibold ${submissionLimitReached ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                  Submissions: {isUnlimited ? `${submissionsToday} (Unlimited)` : `${submissionsToday}/${maxSubmissionsPerDay}`}
                </span>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              {!isLoggedIn && (
                <p className="text-textMuted">
                  Viewing as guest.
                  <button onClick={() => navigate("/register")} className="ml-1 font-semibold text-primary hover:underline">
                    Create a free account
                  </button>{" "}
                  to submit text.
                </p>
              )}
            </div>
            {wordLimitExceeded && <p className="mt-3 text-sm font-medium text-red-600">Word limit exceeded for your current plan.</p>}
            {submissionLimitReached && <p className="mt-3 text-sm font-medium text-red-600">Daily submission limit reached. Upgrade to continue.</p>}
            {error && <p className="mt-3 text-sm font-medium text-red-600">{error}</p>}
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="ui-card flex flex-col p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-secondary">Input Text</h2>
                <button onClick={clearInput} disabled={!inputText} className="btn-ghost text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear
                </button>
              </div>
              <textarea
                rows={12}
                placeholder="Paste or type your text here..."
                value={inputText}
                onChange={handleInputChange}
                className="ui-textarea flex-1"
              />
              <button
                onClick={handleSubmitClick}
                disabled={loadingDecipher || !inputText.trim() || wordLimitExceeded}
                className={`mt-6 w-full py-3.5 text-base ${submissionLimitReached ? "btn-warning" : "btn-primary"}`}
              >
                {loadingDecipher ? "Processing..." : submissionLimitReached ? "Upgrade for More Submissions" : "Decipher Text"}
              </button>
            </div>

            <div className="ui-card flex flex-col p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-secondary">Deciphered Output</h2>
                <button onClick={copyOutput} disabled={!outputText} className="btn-ghost text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </button>
              </div>
              <textarea
                rows={12}
                readOnly
                placeholder="Your original text style, with detector-triggering patterns removed, will appear here..."
                value={outputText}
                className="ui-textarea flex-1 bg-slate-50"
              />
            </div>
          </div>

          {outputText && isLoggedIn && plan !== "premium" && (
            <div className="ui-card mt-8 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">
                  Keep momentum
                </p>
                <h3 className="mt-1 text-xl font-bold text-secondary">
                  Unlock {upgradeTargetPlan} for higher daily limits and faster throughput
                </h3>
                <p className="mt-1 text-sm text-textMuted">
                  You already got value from this run. Scale your workflow without hitting free-tier ceilings.
                </p>
              </div>
              <button
                onClick={() => goToPricing("post_output_upgrade")}
                className="btn-primary whitespace-nowrap px-5 py-3 text-sm"
              >
                Upgrade to {upgradeTargetPlan}
              </button>
            </div>
          )}

          {pageType === "default" && (
            <section className="mt-14 space-y-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-secondary">Why DeCipher works better</h2>
                  <p className="mt-1 text-sm text-textMuted">
                    Cleaner output quality with stronger readability and detector-focused structure control.
                  </p>
                </div>
                <div className="inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-secondary shadow-sm">
                  Processed texts (this browser): {sessionProcessedCount}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {TRUST_SIGNALS.map((signal) => (
                  <div
                    key={signal.title}
                    className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 shadow-soft"
                  >
                    <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-primary/10 blur-xl" />
                    <div className="relative">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-2xl">{signal.icon}</span>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                          {signal.tag}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-secondary">{signal.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-textMuted">{signal.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ui-card overflow-hidden">
                <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-4">
                  <h3 className="text-xl font-bold text-secondary">User Feedback</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                   Don't take our word for it see what our users are saying about DeCipher. 
                  </p>
                </div>
                <div className="p-5 sm:p-7">
                  <blockquote className="text-base leading-relaxed text-secondary sm:text-lg">
                    "{activeReview.quote}"
                  </blockquote>
                  <div className="mt-5">
                    <p className="font-semibold text-secondary">{activeReview.name}</p>
                    <p className="text-sm text-textMuted">{activeReview.role}</p>
                    <p className="mt-1 text-xs text-amber-700">{activeReview.note}</p>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => goToReview(activeReviewIndex - 1, "prev")}
                        className="btn-secondary px-3 py-2 text-sm"
                        aria-label="Show previous review"
                      >
                        ←
                      </button>
                      <button
                        onClick={() => goToReview(activeReviewIndex + 1, "next")}
                        className="btn-secondary px-3 py-2 text-sm"
                        aria-label="Show next review"
                      >
                        →
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      {REVIEW_CAROUSEL_ITEMS.map((item, index) => (
                        <button
                          key={item.quote}
                          onClick={() => goToReview(index, "dot")}
                          className={`h-2.5 rounded-full transition ${
                            index === activeReviewIndex ? "w-7 bg-primary" : "w-2.5 bg-slate-300 hover:bg-slate-400"
                          }`}
                          aria-label={`Show review ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {pageType === "competitor" && pageKey && (
            <div className="ui-card mt-14 p-6 sm:p-8">
              <h3 className="mb-6 text-center text-2xl font-bold text-secondary">
                Why Creators Switch to DeCipher
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-[640px] w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-sm text-textMuted">
                      <th className="pb-4 font-semibold">Features & Value Metrics</th>
                      <th className="pb-4 font-bold text-primary">DeCipher Engine</th>
                      <th className="pb-4 font-semibold capitalize">{pageKey.replace("-", " ")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-secondary">
                    <tr>
                      <td className="py-4 font-medium">Pricing Profile</td>
                      <td className="py-4 font-semibold text-primary">Starts at $5.99/mo</td>
                      <td className="py-4 text-textMuted">Expensive Tier Restrictions</td>
                    </tr>
                    <tr>
                      <td className="py-4 font-medium">Text Readability Processing</td>
                      <td className="py-4 font-semibold text-primary">Natural Human Cadence</td>
                      <td className="py-4 text-textMuted">Prone to Fluff & Repetition</td>
                    </tr>
                    <tr>
                      <td className="py-4 font-medium">Data Storage Safeguards</td>
                      <td className="py-4 font-semibold text-primary">Zero Logs Kept</td>
                      <td className="py-4 text-textMuted">Potential Database Caching</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showAuthModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4">
              <div className="ui-card w-full max-w-md p-8 text-center">
                <h2 className="mb-3 text-2xl font-bold text-secondary">Create an account to start</h2>
                <p className="mb-7 text-sm text-textMuted">
                  Sign up for free to submit text, manage limits, and save your workflow.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      trackEvent("auth_modal_cta_clicked", { action: "register", page_type: pageType });
                      navigate("/register");
                    }}
                    className="btn-primary w-full"
                  >
                    Create Free Account
                  </button>
                  <button
                    onClick={() => {
                      trackEvent("auth_modal_cta_clicked", { action: "login", page_type: pageType });
                      navigate("/login");
                    }}
                    className="btn-secondary w-full"
                  >
                    I already have an account
                  </button>
                </div>
                <button
                  onClick={() => {
                    trackEvent("auth_modal_dismissed", { page_type: pageType });
                    setShowAuthModal(false);
                  }}
                  className="mt-5 text-sm font-medium text-textMuted transition hover:text-secondary"
                >
                  Maybe later
                </button>
              </div>
            </div>
          )}

          <div className="mx-auto mt-14 max-w-3xl">
            <h2 className="mb-8 text-center text-3xl font-bold text-secondary">Frequently Asked Questions</h2>
            <div className="space-y-3 text-left">
              {faqItems.map((faq, index) => (
                <details
                  key={faq.q}
                  className="ui-card group overflow-hidden"
                  onToggle={(e) => {
                    const target = e.currentTarget as HTMLDetailsElement;
                    if (target.open) {
                      trackEvent("faq_opened", {
                        page_type: pageType,
                        question: faq.q,
                      });
                    }
                  }}
                  {...(index === 0 ? { open: true } : {})}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-base font-semibold text-secondary">
                    <span className="pr-3">{faq.q}</span>
                    <span className="text-xl text-textMuted transition group-open:rotate-180">⌄</span>
                  </summary>
                  <div className="border-t border-slate-100 px-5 py-4 leading-relaxed text-textMuted">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}